import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import uniqid from 'uniqid';
import { useNavigate } from 'react-router-dom';
import {
  Box, TableCell, Text,
} from 'grommet';
import { CircleInformation } from 'grommet-icons';
import {
  DropButton,
  AppButton,
  SimpleModal,
  DomainDetails,
  MatchStatsDetails,
} from '@Components/Control';
import { ProductFormCheckBoxInput, ProductFormSingleTextInput } from '@Components/Partial/Product/NewSearch';
import { useAppTheme } from '@Components/Context';
import {
  buildDateTimeLabel, formatUnit, shortenInt,
} from '@Helpers/util';
import {
  StyledPagedTableRowLabel,
  StyledPagedTableUrl,
  StyledDetailTableRow,
  StyledDropMenu,
  StyledDetailDropMenu,
} from './StyledDashboard';
import {
  StyledFlexibleDrop,
  StyledSelectMenu,
  StyledSVG,
  StyledSeeDetailsButton,
} from '../StyledSharedComponents';


export const renderText = (text, size, textColor) => (
  <StyledPagedTableRowLabel
    key={uniqid()}
    size={size}
    weight={400}
    color={textColor}
  >
    {text}
  </StyledPagedTableRowLabel>
);

export const renderUrl = (value) => <StyledPagedTableUrl color="var(--page-url-color)" href={value} target="_blank">{value}</StyledPagedTableUrl>;

export const renderDetailDropdown = (cellData) => {
  if (cellData.moreDetails) {
    return (
      <DropButton
        key={uniqid()}
        overridePosition={{ top: 'bottom' }}
        withCloseDropContent={(onClose) => (
          <Box round="var(--app-radius)" pad="small">
            <StyledDetailDropMenu direction="column" gap="0.5rem" onClick={() => onClose()}>
              <Text weight={600}>{cellData.moreDetails.summary}</Text>
              <Box direction="row" gap="0.25rem">
                {cellData.moreDetails.details.map((dt) => (
                  <Box key={uniqid()} width={{ min: '15.25rem' }}>
                    <Text>{dt}</Text>
                  </Box>
                ))}
              </Box>
            </StyledDetailDropMenu>
          </Box>
        )}
      >
        {() => (
          <AppButton
            dropDown
            noPadLeft
            tableButtonStyle
            alignSelf="start"
            justify="start"
            color="var(--button-table-text)"
            fontWeight={600}
            label={cellData.value}
            icon={<CircleInformation color="var(--button-table-icon)" size="0.9rem" />}
            focusHighlight="var(--page-focus-highlight)"
          />
        )}
      </DropButton>
    );
  }

  return cellData.value;
};

export const renderPopup = (cellData, watermarkGroupingKey = null) => {
  const popupData = (cellData && typeof cellData === 'object' && cellData.display)
    ? cellData.display
    : cellData;

  return (
    <MatchStatsDetails
      small={false}
      statsData={popupData}
      textColor="var(--page-modal-text)"
      buttonHighlight="var(--page-highlight)"
      fallbackHeader="Match stats"
      buttonLabel="View stats"
      watermarkGroupingKey={watermarkGroupingKey}
    />
  );
};

export const renderMultiUrls = (
  small, cellData, dmcaListsEnabled, handleDomainUpdate, hostingData = null, includeISPData = false,
) => {
  const primaryUrl = cellData.url;
  const extraUrls = [
    { label: 'Post URL', url: cellData.postUrl },
    // Hiding reference URL and secondary URL for now
    // { label: 'Reference URL', url: cellData.referenceUrl },
    // { label: 'Secondary URL', url: cellData.secondaryUrl },
    { label: 'Link URL', url: cellData.linkUrl },
  ].filter(({ url }) => url);

  if (!primaryUrl) return null;

  return (
    <Box direction="column" key={uniqid()}>
      <DomainDetails
        small={small}
        modalTitle="URL details"
        primaryUrl={primaryUrl}
        extraUrls={extraUrls}
        dmcaListsEnabled={dmcaListsEnabled}
        handleDomainUpdate={handleDomainUpdate}
        hostingData={hostingData}
        includeISPData={includeISPData}
      />
    </Box>
  );
};

const findDisplayValue = (statusOptions, value) => {
  const foundValue = _.find(statusOptions, { api: value });

  if (value === 'pending') {
    return 'Pending';
  }

  if (foundValue && foundValue.display) {
    return foundValue.display;
  }

  return foundValue;
};

export const DomainStatusInput = ({
  small,
  bulkUpdateReviewStatusByDomain,
  reviewStatusOptions,
}) => {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [dropOpen, setDropOpen] = React.useState(false);
  /* eslint-disable camelcase */
  const [query, setQuery] = React.useState({
    domain: '',
    review_status: null,
    apply_to_all: false,
  });
  const { theme: { page, button } } = useAppTheme();

  const handleSubmit = () => {
    if (query.domain && query.review_status) {
      bulkUpdateReviewStatusByDomain(query);
      setQuery({ domain: '', review_status: null });
    }
  };

  const handleDomainChange = (value) => (
    setQuery((prev) => ({
      ...prev,
      domain: value,
    }))
  );

  const handleSelect = (option, onClose) => {
    setQuery((prev) => ({
      ...prev,
      review_status: option.api,
    }));
    onClose();
  };

  const displayStatus = query.review_status ? findDisplayValue(reviewStatusOptions, query.review_status) : 'Select status';
  const submitDisabled = !query.domain || !query.review_status;

  return (
    <Box>
      <StyledFlexibleDrop
        formIcons
        bgColor={page.inputBg}
        height="var(--table-controls-height)"
        highlight={page.focusHighlight}
        onOpen={() => setDropOpen(true)}
        onClose={() => setDropOpen(false)}
        isOpen={dropOpen}
        withCloseDropContent={(onClose) => (
          <Box round="var(--app-radius)" pad="12px" background={page.inputBg}>
            <StyledSelectMenu
              plain
              label="Apply review status by domain"
              onClick={() => {
                setModalOpen(true);
                onClose();
              }}
              hoverIndicator={button.hoverBg}
              highlightText={button.hoverText}
            />
          </Box>
        )}
      >
        {(Icon) => (
          <AppButton
            fill
            dropDown
            textSize="1rem"
            color={page.inputText}
          >
            <Box direction="row" gap="1rem" width="100%">
              <Text color={page.inputText} size={small ? '0.8rem' : '0.9rem'}>
                More actions
              </Text>
              <Box pad={{ top: '0.15rem' }}>
                <Icon color={page.inputIcon} size="0.9rem" />
              </Box>
            </Box>
          </AppButton>
        )}
      </StyledFlexibleDrop>
      {modalOpen && (
        <SimpleModal
          hideLowerSection
          noClickOutside
          small={small}
          toggleModal={setModalOpen}
          modalTitle="Apply review status by domain"
          desktopMinWidth="30rem"
        >
          <Box direction="column" gap="1rem" pad={{ top: '0.5rem' }}>
            <Text size="1rem" color={page.modalText} style={{ lineHeight: 1.5 }}>
              Enter a domain and select a status to apply it to all results from that domain.
              If you select &lsquo;Apply status to previously reviewed non-infringing
              results&lsquo;, the status will also be applied to results that have previously
              been reviewed with a non-infringing status, otherwise it will only apply
              to results with &lsquo;pending&rsquo; status.
            </Text>
            <Box direction="row" gap="0.5rem" align="center" width="100%">
              <ProductFormSingleTextInput
                focusHighlight={page.focusHighlight}
                handleFormValues={handleDomainChange}
                value={query.domain}
                placeholder="Enter a domain e.g. examplesite.com"
              />
            </Box>
            <Box direction="row" gap="0.5rem" align="center" width="100%">
              <ProductFormCheckBoxInput
                small={small}
                key={uniqid()}
                bgColor={page.inputBg}
                borderColor={page.inputBorder}
                checkmarkColor={page.highlight}
                focusHighlight={page.focusHighlight}
                value={query.apply_to_all}
                handleFormValues={() => setQuery((prev) => ({
                  ...prev,
                  apply_to_all: !prev.apply_to_all,
                }))}
                label="Apply status to previously reviewed non-infringing results"
                labelColor={page.modalText}
                customBoxSize="1.25rem"
                customCheckmarkSize="1.1rem"
              />
            </Box>
            <Box direction="row" align="center" justify="start" gap="1rem">
              <Box width="fit-content" flex={{ grow: 1 }}>
                <DropButton
                  withCloseDropContent={(onClose) => (
                    <CellDropMenu
                      options={reviewStatusOptions}
                      onClick={(option) => handleSelect(option, onClose)}
                    />
                  )}
                >
                  {(Icon) => (
                    <AppButton
                      dropDown
                      tableButtonStyle
                      color={button.tableText}
                      fontWeight={600}
                      label={displayStatus}
                      icon={<Icon color={button.tableIcon} size="0.9rem" />}
                      focusHighlight={page.focusHighlight}
                      isOpen={dropOpen}
                    />
                  )}
                </DropButton>
              </Box>
              <AppButton
                overrideHover
                onClick={() => {
                  handleSubmit();
                  setModalOpen(false);
                }}
                disabled={submitDisabled}
                textSize="0.9rem"
                level="tableControls"
                color={button.primaryText}
                bgColor={button.primaryBg}
                fontWeight={600}
                label="Apply status"
              />
            </Box>
          </Box>
        </SimpleModal>
      )}
    </Box>
  );
  /* eslint-enable camelcase */
};

DomainStatusInput.propTypes = {
  small: PropTypes.bool.isRequired,
  bulkUpdateReviewStatusByDomain: PropTypes.func.isRequired,
  reviewStatusOptions: PropTypes.arrayOf(PropTypes.shape({
    display: PropTypes.string.isRequired,
    api: PropTypes.string.isRequired,
  })).isRequired,
};

export const CellDropMenu = ({ options, onClick }) => (
  <Box round="6px" pad="small" background="var(--page-input-bg)">
    {options.map((option) => (
      <StyledDropMenu
        plain
        key={uniqid()}
        label={option.display}
        onClick={() => onClick(option)}
        textColor="var(--page-input-text)"
        hoverTextColor="var(--button-hover-text)"
        hoverIndicator="var(--button-hover-bg)"
      />
    ))}
  </Box>
);

CellDropMenu.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    display: PropTypes.string.isRequired,
    api: PropTypes.string.isRequired,
  }).isRequired).isRequired,
  onClick: PropTypes.func.isRequired,
};

export const ClickableTableCell = ({
  colUid,
  size,
  content = null,
  editableOptions = null,
  editableId = null,
  reviewValue = null,
  updateRowReviewStatus,
  colSpan = null,
  withStatus = false,
}) => {
  const initialValue = reviewValue === 'None' ? { display: 'None', api: 'none' } : reviewValue;
  const { theme: { page, button } } = useAppTheme();
  const [dropOpen, setDropOpen] = React.useState(false);

  if ((colUid === 'reviewStatus')) {
    const [selectedOption, setSelectedOption] = React.useState(initialValue);

    const handleSelect = (option, onClose) => {
      setSelectedOption(option);
      updateRowReviewStatus(editableId, option.api);
      onClose();
    };

    return (
      <TableCell key={colUid} align={withStatus ? 'end' : 'start'} size={size} colSpan={colSpan}>
        <Box direction="row" gap="1rem" align="center" justify={withStatus ? 'end' : 'start'}>
          {withStatus && (
            <Box>
              <Text weight={500} color={page.tableHeader}>Status:</Text>
            </Box>
          )}
          <DropButton
            onOpen={() => setDropOpen(true)}
            onClose={() => setDropOpen(false)}
            withCloseDropContent={(onClose) => (
              <CellDropMenu
                options={editableOptions}
                onClick={(option) => handleSelect(option, onClose)}
              />
            )}
          >
            {(Icon) => (
              <AppButton
                dropDown
                tableButtonStyle
                color={button.tableText}
                fontWeight={600}
                label={selectedOption.display || findDisplayValue(editableOptions, reviewValue)}
                icon={<Icon color={button.tableIcon} size="0.9rem" />}
                focusHighlight={page.focusHighlight}
                isOpen={dropOpen}
              />
            )}
          </DropButton>
        </Box>
      </TableCell>
    );
  }

  if (colUid === 'setReviewStatus') {
    return (
      <TableCell key={colUid} align="end" size={size} colSpan={colSpan}>
        <Box direction="row" gap="1rem" align="center" justify="end">
          <Box>
            <Text weight={500} color={page.tableHeader}>Status:</Text>
          </Box>
          {content || ''}
        </Box>
      </TableCell>
    );
  }

  return (
    <TableCell key={colUid} align="start" size={size}>
      {content || ''}
    </TableCell>
  );
};

ClickableTableCell.propTypes = {
  colUid: PropTypes.string.isRequired,
  size: PropTypes.string.isRequired,
  content: PropTypes.shape({}),
  editableOptions: PropTypes.arrayOf(PropTypes.shape({
    api: PropTypes.string.isRequired,
    display: PropTypes.string.isRequired,
  })),
  editableId: PropTypes.string,
  reviewValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  updateRowReviewStatus: PropTypes.func.isRequired,
  colSpan: PropTypes.number,
  withStatus: PropTypes.bool,
};

export const DetailTableRow = ({
  isSmall,
  header,
  rowData,
  updateRowReviewStatus,
  selectedStatuses,
  handleRowSelect,
  selectedRowIds,
  showSpacerElement,
  handleDomainUpdate,
  dmcaListsEnabled = false,
  watermarkGroupingKey = null,
  resultId = null,
  hostingData = null,
  includeISPData = false,
}) => {
  const navigate = useNavigate();
  const { theme: { page }, icons } = useAppTheme();
  const noDataPlaceholder = (val) => ((val === 'pending') ? '...' : ' ');

  const editableRow = rowData.find((row) => row.editable && row.editable === 'dropdown');

  const selectReviewValue = ({ editable, api }) => {
    if (selectedStatuses && selectedStatuses.length > 0 && editable === 'dropdown') {
      const currentStatus = selectedStatuses.find((dt) => dt.id === editableRow.editableId);

      return currentStatus.review_status;
    }

    return api;
  };

  const renderCellContent = ({ display, decoration, unit }) => {
    const formattedDisplay = unit ? shortenInt(display) : display;
    const cellData = unit ? `${formattedDisplay ?? '0'} ${formatUnit(unit, display)}` : display;
    let overrideDecoration = false;

    if (display && typeof display === 'object' && decoration !== 'popup') {
      overrideDecoration = true;
    }

    let renderDecor = (val) => val;
    if (overrideDecoration && cellData !== null) {
      return renderMultiUrls(isSmall, cellData, dmcaListsEnabled, handleDomainUpdate, hostingData, includeISPData);
    }

    if (decoration === 'datetime') {
      renderDecor = buildDateTimeLabel;
    } else if (decoration === 'details/dropdown') {
      renderDecor = renderDetailDropdown;
    } else if (decoration === 'url') {
      renderDecor = renderUrl;
    }

    return decoration === 'popup'
      ? renderPopup(display, watermarkGroupingKey)
      : renderText(((cellData !== null && cellData !== 'pending') ? renderDecor(cellData) : noDataPlaceholder(cellData)), '0.9rem', page.tableText);
  };

  const options = rowData.find(({ editable }) => editable === 'dropdown');
  const filteredData = _.filter(rowData, ((dt) => dt.editable !== 'dropdown' && !dt.api));
  const setReviewStatus = rowData.find(({ api, editable }) => !editable && api);
  const useData = isSmall ? filteredData : rowData;

  return (
    <>
      <StyledDetailTableRow rowColor={page.tableBg}>
        {!isSmall && editableRow && (
          <TableCell key={uniqid()} align="start" size="auto" style={{ marginLeft: '-11px' }}>
            <ProductFormCheckBoxInput
              small={isSmall}
              key={uniqid()}
              bgColor={page.inputBg}
              borderColor={page.inputBorder}
              checkmarkColor={page.highlight}
              focusHighlight={page.focusHighlight}
              value={selectedRowIds.includes(editableRow.editableId)}
              handleFormValues={() => handleRowSelect(editableRow.editableId)}
              customBoxSize="1.25rem"
              customCheckmarkSize="1.1rem"
            />
          </TableCell>
        )}
        {!isSmall && !editableRow && showSpacerElement && (
          <TableCell key={uniqid()} align="start" size="auto" style={{ marginLeft: '-11px' }} />
        )}
        {useData.map((dt) => (
          <React.Fragment key={uniqid()}>
            <ClickableTableCell
              colUid={dt.editable && dt.editable === 'dropdown' ? 'reviewStatus' : uniqid()}
              align="start"
              size="auto"
              content={renderCellContent(dt)}
              editableOptions={dt.editableOptions}
              editableId={dt.editableId}
              reviewValue={selectReviewValue(dt)}
              updateRowReviewStatus={updateRowReviewStatus}
            />
          </React.Fragment>
        ))}
        {!isSmall && resultId && (
          <TableCell key={uniqid()} align="end" size="auto">
            <StyledSeeDetailsButton
              plain
              textColor={page.tableText}
              hoverTextColor={page.highlight}
              onClick={() => navigate(`/infringements/${resultId}`)}
            >
              <Box wrap={false} direction="row" gap="0.5rem" align="center">
                <Text size="1rem">See details</Text>
                <Box flex={false} width="1rem" height="1rem">
                  <StyledSVG
                    $fillColor={page.tableText}
                    $hoverColor={page.highlight}
                    src={icons.seeDetailsIcon}
                    width="1rem"
                    height="1rem"
                  />
                </Box>
              </Box>
            </StyledSeeDetailsButton>
          </TableCell>
        )}
      </StyledDetailTableRow>
      {isSmall && options && (
        <StyledDetailTableRow isLast rowColor={page.tableBg}>
          <ClickableTableCell
            withStatus
            colUid="reviewStatus"
            size="auto"
            alignCell="end"
            colSpan={header.length}
            content={renderCellContent({ display: 'Status', decoration: 'editable/dropdown' })}
            editableOptions={options.editableOptions}
            editableId={options.editableId}
            reviewValue={selectReviewValue(options)}
            updateRowReviewStatus={updateRowReviewStatus}
          />
        </StyledDetailTableRow>
      )}
      {isSmall && !options && (
        <StyledDetailTableRow isLast rowColor={page.tableBg}>
          <ClickableTableCell
            withStatus
            colUid="setReviewStatus"
            size="auto"
            alignCell="end"
            colSpan={header.length}
            content={renderCellContent(setReviewStatus)}
            reviewValue={null}
            updateRowReviewStatus={updateRowReviewStatus}
          />
        </StyledDetailTableRow>
      )}
    </>
  );
};

/* eslint-disable camelcase */
DetailTableRow.propTypes = {
  isSmall: PropTypes.bool.isRequired,
  header: PropTypes.arrayOf(PropTypes.shape({
    display: PropTypes.string.isRequired,
  })).isRequired,
  rowData: PropTypes.arrayOf(PropTypes.shape({
    api: PropTypes.string,
    decoration: PropTypes.any,
    display: PropTypes.any,
    editable: PropTypes.string,
    editableId: PropTypes.string,
    editableOptions: PropTypes.arrayOf(PropTypes.shape({
      api: PropTypes.string.isRequired,
      display: PropTypes.string.isRequired,
    })),
    unit: PropTypes.string,
  })).isRequired,
  selectedStatuses: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    review_status: PropTypes.string.isRequired,
  })).isRequired,
  updateRowReviewStatus: PropTypes.func.isRequired,
  handleRowSelect: PropTypes.func.isRequired,
  selectedRowIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  showSpacerElement: PropTypes.bool.isRequired,
  dmcaListsEnabled: PropTypes.bool,
  handleDomainUpdate: PropTypes.func.isRequired,
  watermarkGroupingKey: PropTypes.string,
  resultId: PropTypes.string,
  hostingData: PropTypes.arrayOf(PropTypes.shape({ host: PropTypes.string })),
  includeISPData: PropTypes.bool,
};
/* eslint-enable camelcase */
